import { NgClass } from '@angular/common';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, viewChild } from '@angular/core';
import { Modal } from 'flowbite';
import {
  IClient,
  IClientNotificationEventRow,
  IClientNotificationOptions,
  IClientNotificationRule,
  INotificationCategoryGroup,
  INotificationPreference,
  INotificationText,
} from 'src/app/models';
import {
  NotificationChannel,
  NotificationRecipient,
  NotificationSettingsMode,
  NotificationTypes,
  UserRole,
} from 'src/app/models/enums';
import { NotificationSettingsService, StorageService, UsersService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import { TooltipPosition } from '../../tooltip/tooltip-position';
import { TooltipModule } from '../../tooltip/tooltip.module';
import { CheckboxComponent } from '../../input/checkbox/checkbox.component';
import { NotificationTextsEditorComponent } from './notification-texts-editor/notification-texts-editor.component';

@Component({
  selector: 'app-modal-notification-settings',
  templateUrl: './modal-notification-settings.component.html',
  styleUrls: ['./modal-notification-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, CheckboxComponent, TooltipModule, NotificationTextsEditorComponent],
})
export class ModalNotificationSettingsComponent {
  public NotificationSettingsMode = NotificationSettingsMode;
  public images = images;
  public TooltipPosition = TooltipPosition;

  public mode: NotificationSettingsMode = NotificationSettingsMode.OWN;
  public clients: IClient[] = [];
  public selectedClientId?: number;
  public canEditClientRules = false;
  public loading = false;
  public saving = false;

  public preferenceGroups: INotificationCategoryGroup<INotificationPreference>[] = [];
  public eventRowGroups: INotificationCategoryGroup<IClientNotificationEventRow>[] = [];
  public recipients: NotificationRecipient[] = [];
  public notificationTexts: INotificationText[] = [];
  public editingTextsEvent?: string;
  public clientOptions?: IClientNotificationOptions;

  private modal?: Modal;
  private readonly modalElement = viewChild.required<ElementRef<HTMLElement>>('notificationSettingsModal');
  private preferences: INotificationPreference[] = [];
  private rules: IClientNotificationRule[] = [];
  private eventsWithTexts = new Set<string>();
  private savedClientOptions?: IClientNotificationOptions;

  constructor(
    public literalService: LiteralService,
    private notificationSettingsService: NotificationSettingsService,
    private usersService: UsersService,
    private storageService: StorageService,
    private utilsService: UtilsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  async showNotificationSettingsModal(mode: NotificationSettingsMode, clientId?: number) {
    this.mode = mode;
    this.editingTextsEvent = undefined;
    this.modal = new Modal(this.modalElement().nativeElement, {
      placement: 'center',
      closable: !this.utilsService.isTouchDevice(),
    });
    this.modal.show();
    this.loading = true;
    try {
      const role = await this.storageService.getRole();
      this.canEditClientRules = role === UserRole.ADMIN || role === UserRole.SUPERADMIN;
      this.clients = clientId ? [] : await this.usersService.getClients();
      this.selectedClientId = clientId ?? this.selectedClientId ?? this.clients[0]?.id;
      await this.loadSettings();
    } finally {
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  close() {
    this.modal?.hide();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) this.close();
  }

  async selectClient(clientId: string) {
    this.selectedClientId = Number(clientId);
    this.editingTextsEvent = undefined;
    this.loading = true;
    try {
      await this.loadSettings();
    } finally {
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  isChannelMuted(preference: INotificationPreference, channel: NotificationChannel) {
    return preference.mutedChannels.includes(channel);
  }

  togglePreferenceChannel(preference: INotificationPreference, channel: NotificationChannel) {
    preference.mutedChannels = this.isChannelMuted(preference, channel)
      ? preference.mutedChannels.filter((mutedChannel) => mutedChannel !== channel)
      : [...preference.mutedChannels, channel];
  }

  toggleRuleChannel(rule: IClientNotificationRule, channel: NotificationChannel) {
    rule.channels = rule.channels.includes(channel)
      ? rule.channels.filter((enabledChannel) => enabledChannel !== channel)
      : [...rule.channels, channel];
  }

  getChannelLabel(channel: NotificationChannel) {
    return this.literalService.get(`notificationSettings.channels.${channel}`);
  }

  toggleCreateDelayIncidents() {
    if (this.clientOptions) this.clientOptions.createDelayIncidents = !this.clientOptions.createDelayIncidents;
  }

  hasTexts(event: string) {
    return this.eventsWithTexts.has(event);
  }

  openTextsEditor(event: string) {
    this.editingTextsEvent = event;
  }

  closeTextsEditor() {
    this.editingTextsEvent = undefined;
  }

  setTexts(texts: INotificationText[]) {
    this.notificationTexts = texts;
    this.eventsWithTexts = new Set(texts.flatMap((text) => text.events));
  }

  async save() {
    this.saving = true;
    try {
      if (this.mode === NotificationSettingsMode.CLIENT && this.selectedClientId) {
        await this.saveClientSettings(this.selectedClientId);
      } else {
        this.setPreferences(
          await this.notificationSettingsService.updateOwnPreferences(this.preferences, this.selectedClientId),
        );
      }
      this.utilsService.showNotification(
        images.notifications.check,
        'notificationSettings.saved',
        NotificationTypes.SUCCESS,
      );
      this.close();
    } catch {
      this.utilsService.showNotification(
        images.notifications.warning,
        'notificationSettings.error',
        NotificationTypes.DANGER,
      );
    } finally {
      this.saving = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private async loadSettings() {
    if (this.mode === NotificationSettingsMode.CLIENT) {
      if (this.selectedClientId) await this.loadClientSettings(this.selectedClientId);
    } else {
      this.setPreferences(await this.notificationSettingsService.getOwnPreferences(this.selectedClientId));
    }
  }

  private async loadClientSettings(clientId: number) {
    const [rules, texts, options] = await Promise.all([
      this.notificationSettingsService.getClientRules(clientId),
      this.canEditClientRules ? this.notificationSettingsService.getClientTexts(clientId) : Promise.resolve([]),
      this.canEditClientRules ? this.notificationSettingsService.getClientOptions(clientId) : Promise.resolve(undefined),
    ]);
    this.setRules(rules);
    this.setTexts(texts);
    this.setClientOptions(options);
  }

  private async saveClientSettings(clientId: number) {
    const clientOptionsChanged =
      !!this.clientOptions &&
      this.clientOptions.createDelayIncidents !== this.savedClientOptions?.createDelayIncidents;
    const [rules, options] = await Promise.all([
      this.notificationSettingsService.updateClientRules(clientId, this.rules),
      clientOptionsChanged
        ? this.notificationSettingsService.updateClientOptions(clientId, this.clientOptions!)
        : Promise.resolve(this.savedClientOptions),
    ]);
    this.setRules(rules);
    this.setClientOptions(options);
  }

  private setClientOptions(options?: IClientNotificationOptions) {
    this.savedClientOptions = options;
    this.clientOptions = options ? { ...options } : undefined;
  }

  private setRules(rules: IClientNotificationRule[]) {
    this.rules = rules;
    this.eventRowGroups = this.notificationSettingsService.toEventRows(rules);
    const presentRecipients = new Set(rules.map((rule) => rule.recipient));
    this.recipients = Object.values(NotificationRecipient).filter((recipient) => presentRecipients.has(recipient));
  }

  private setPreferences(preferences: INotificationPreference[]) {
    this.preferences = preferences;
    this.preferenceGroups = this.notificationSettingsService.groupByCategory(preferences);
  }
}
